import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'

export type KnifePoint = { x: number; y: number }

export type SliceableWire = {
  id: string
  sourceId: string
  targetId: string
  from: KnifePoint
  to: KnifePoint
  points?: KnifePoint[]
}

type VectorGestureKnifeProps = {
  isActive: boolean
  wires: SliceableWire[]
  onSlice: (wireIds: string[]) => void
}

const fadeDurationMs = 300

const intersects = (a: KnifePoint, b: KnifePoint, c: KnifePoint, d: KnifePoint) => {
  const det = (b.x - a.x) * (d.y - c.y) - (d.x - c.x) * (b.y - a.y)
  if (det === 0) return false
  const lambda = ((d.y - c.y) * (d.x - a.x) + (c.x - d.x) * (d.y - a.y)) / det
  const gamma = ((a.y - b.y) * (d.x - a.x) + (b.x - a.x) * (d.y - a.y)) / det
  return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1)
}

const distanceToSegment = (point: KnifePoint, start: KnifePoint, end: KnifePoint) => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  if (dx === 0 && dy === 0) return Math.hypot(point.x - start.x, point.y - start.y)
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / ((dx * dx) + (dy * dy))))
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy))
}

const segmentsTouch = (a: KnifePoint, b: KnifePoint, c: KnifePoint, d: KnifePoint) => {
  if (intersects(a, b, c, d)) return true
  return Math.min(
    distanceToSegment(a, c, d),
    distanceToSegment(b, c, d),
    distanceToSegment(c, a, b),
    distanceToSegment(d, a, b),
  ) <= 8
}

export const VectorGestureKnife = ({ isActive, wires, onSlice }: VectorGestureKnifeProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const pointsRef = useRef<KnifePoint[]>([])
  const isDrawingRef = useRef(false)
  const animationFrameRef = useRef<number | null>(null)
  const [viewportStyle, setViewportStyle] = useState<CSSProperties>({})

  useEffect(() => {
    const canvas = canvasRef.current
    const parent = canvas?.closest('.checklistBoardCanvas') as HTMLElement | null
    if (!canvas || !parent) return

    const resize = () => {
      const rect = parent.getBoundingClientRect()
      const scale = window.devicePixelRatio || 1
      canvas.width = Math.max(1, Math.round(rect.width * scale))
      canvas.height = Math.max(1, Math.round(rect.height * scale))
      setViewportStyle({
        position: 'absolute',
        left: parent.scrollLeft,
        top: parent.scrollTop,
        width: rect.width,
        height: rect.height,
      })
      const context = canvas.getContext('2d')
      context?.setTransform(scale, 0, 0, scale, 0, 0)
    }

    resize()
    const animationFrame = window.requestAnimationFrame(resize)
    const settleTimer = window.setTimeout(resize, 250)
    window.addEventListener('resize', resize)
    window.addEventListener('scroll', resize, true)
    const observer = new ResizeObserver(resize)
    observer.observe(parent)
    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.clearTimeout(settleTimer)
      window.removeEventListener('resize', resize)
      window.removeEventListener('scroll', resize, true)
      observer.disconnect()
    }
  }, [isActive])

  const drawTrail = (alpha = 1) => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return
    const points = pointsRef.current
    context.clearRect(0, 0, canvas.width, canvas.height)
    if (points.length < 2) return
    context.save()
    context.globalAlpha = alpha
    const stroke = getComputedStyle(canvas).getPropertyValue('--danger').trim() || 'red'
    context.strokeStyle = stroke
    context.lineWidth = 3
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.shadowColor = stroke
    context.shadowBlur = 10
    context.beginPath()
    context.moveTo(points[0].x, points[0].y)
    points.slice(1).forEach((point) => context.lineTo(point.x, point.y))
    context.stroke()
    context.restore()
  }

  const fadeTrail = () => {
    const start = performance.now()
    const tick = (now: number) => {
      const alpha = Math.max(0, 1 - ((now - start) / fadeDurationMs))
      drawTrail(alpha)
      if (alpha > 0) {
        animationFrameRef.current = window.requestAnimationFrame(tick)
      } else {
        pointsRef.current = []
      }
    }
    animationFrameRef.current = window.requestAnimationFrame(tick)
  }

  const toLocalPoint = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const finishSlice = () => {
    isDrawingRef.current = false
    const points = pointsRef.current
    const slicedWireIds = new Set<string>()

    for (let index = 1; index < points.length; index += 1) {
      const a = points[index - 1]
      const b = points[index]
      for (const wire of wires) {
        const wirePoints = wire.points && wire.points.length >= 2 ? wire.points : [wire.from, wire.to]
        for (let wireIndex = 1; wireIndex < wirePoints.length; wireIndex += 1) {
          if (segmentsTouch(a, b, wirePoints[wireIndex - 1], wirePoints[wireIndex])) {
            slicedWireIds.add(wire.id)
            break
          }
        }
      }
    }

    if (slicedWireIds.size > 0) onSlice([...slicedWireIds])
    fadeTrail()
  }

  useEffect(() => () => {
    if (animationFrameRef.current !== null) window.cancelAnimationFrame(animationFrameRef.current)
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className={`vectorGestureKnifeCanvas ${isActive ? 'isActive' : ''}`}
      style={viewportStyle}
      onPointerDown={(event) => {
        if (!isActive) return
        event.preventDefault()
        event.stopPropagation()
        event.currentTarget.setPointerCapture(event.pointerId)
        isDrawingRef.current = true
        pointsRef.current = [toLocalPoint(event)]
        drawTrail()
      }}
      onPointerMove={(event) => {
        if (!isActive || !isDrawingRef.current) return
        event.preventDefault()
        event.stopPropagation()
        pointsRef.current = [...pointsRef.current, toLocalPoint(event)].slice(-80)
        drawTrail()
      }}
      onPointerUp={(event) => {
        if (!isActive || !isDrawingRef.current) return
        event.preventDefault()
        event.stopPropagation()
        finishSlice()
      }}
      onPointerCancel={() => {
        if (!isDrawingRef.current) return
        isDrawingRef.current = false
        fadeTrail()
      }}
    />
  )
}
